import { CATALOGUE_MODE, TCPOS } from "@constants";
import swal from "@sweetalert/with-react";
import classNames from "classnames";
import { t } from "i18next";
import { snapshot, useSnapshot } from "valtio";

import React from "react";
import Modal from "react-modal";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";

import { store } from "@store";

import { useLocalWays } from "@hooks/useLocalWays";

import { AdvertisingScreen } from "@components/LocalSettings/LocalConfigModalWrapper/AdvertisingScreen";
import { CustomerAccount } from "@components/LocalSettings/LocalConfigModalWrapper/CustomerAccount";
import { InformationModes } from "@components/LocalSettings/LocalConfigModalWrapper/InformationModes";
import { Languages } from "@components/LocalSettings/LocalConfigModalWrapper/Languages";
import { Options } from "@components/LocalSettings/LocalConfigModalWrapper/Options";
import { RegulationModes } from "@components/LocalSettings/LocalConfigModalWrapper/RegulationModes";
import { SalesModes } from "@components/LocalSettings/LocalConfigModalWrapper/SalesModes";
import { MenuSvgIcon } from "@components/SvgIcons/MenuSvgIcon";
import {
    setConnectedUser,
    setLoginKeyboardInputsValues,
} from "@components/Synchronization/store";

import {
    kioskStore,
    setActiveConfigOptions,
    setActiveLanguages,
    setActiveTabTitle,
    setIsAuthenticationModalOpened,
    setIsConfigOptionsActive,
    setIsKeyBoardOpened,
    setIsLanguagesActive,
    setIsLocalConfigModalOpened,
    setIsLocalSettingModalOpened,
    setKioskWays,
    setNumericKeyboardInputValue,
    setProject,
    setQrCodeValue,
} from "@pages/Kiosk/store";

import ConfirmationSystemActionsModelWrapper from "../ConfirmationSystemActionsModalWrapper";
import { MenuModalWrapper } from "../MenuModalWrapper";
import "./index.css";
import LocalSettingFooter from "./LocalSettingFooter";
import LocalSettingHeader from "./LocalSettingHeader";
import { Tcpos } from "./Tcpos";

type TabsMenuType = {
    title: string;
    content: React.ReactNode;
};
enum systemActionsType {
    NoAction = "NoAction",
    Reset = "reset",
    ChangeMiddleware = "Change Middleware",
    Active = "pause",
    Update = "update",
    Restart = "restart",
    Close = "close",
    SwitchOff = "switch off",
}
export function LocalConfigModalWrapper(): JSX.Element {
    const { posEditor } = useSnapshot(store);
    const { isLocalConfigModalOpened, localProject } = useSnapshot(kioskStore);
    const localKioskWays = useLocalWays(localProject);
    const [isMenuModalOpened, setIsMenuModalOpened] =
        React.useState<boolean>(false);
    const [activeTab, setActiveTab] = React.useState<number>(0);
    const [systemActions, setSystemActions] = React.useState<systemActionsType>(
        systemActionsType.NoAction
    );
    const [isSystemConfirmationOpen, setIsSystemConfirmationOpen] =
        React.useState<boolean>(false);

    const tabs: TabsMenuType[] = [
        {
            title: t("Options"),
            content: <Options />,
        },
        {
            title: t("Sales methods"),
            content: <SalesModes />,
        },
        {
            title: t("Regulation modes"),
            content: <RegulationModes />,
        },
        {
            title: t("Information modes"),
            content: <InformationModes />,
        },
        {
            title: t("Customer account"),
            content: <CustomerAccount />,
        },
        {
            title: t("Languages"),
            content: <Languages />,
        },
        {
            title: t("Advertising screen"),
            content: <AdvertisingScreen />,
        },
    ];

    if (posEditor === TCPOS) {
        const additionalTab = {
            title: t("TCPOS"),
            content: <Tcpos />,
        };
        tabs.push(additionalTab);
    }
    function toggleNav(tab: number) {
        if (activeTab !== tab) {
            setActiveTab(tab);
        }
    }

    const handleCloseModal = () => {
        setIsAuthenticationModalOpened(false);
        setIsLocalSettingModalOpened(false);
        setIsLocalConfigModalOpened(false);
        setIsMenuModalOpened(false);
        setQrCodeValue("");
        setLoginKeyboardInputsValues({
            email: "",
            password: "",
        });
        setNumericKeyboardInputValue("");
        setIsKeyBoardOpened(false);
        setConnectedUser({});
    };

    function handleOnClickEvent() {
        const { localProject } = snapshot(kioskStore);
        console.log(
            "Update Setting project with Local Setting with Local project:",
            JSON.stringify(localProject)
        );

        setConnectedUser({});
        setIsConfigOptionsActive(
            (
                (localProject.template.content as ProjectContentType)
                    .options as OptionsType
            ).active
        );

        setActiveConfigOptions(
            (
                (localProject.template.content as ProjectContentType)
                    .options as OptionsType
            ).items.filter((option) => option.active === true)
        );

        setIsLanguagesActive(
            (
                (localProject.template.content as ProjectContentType)
                    .languages as LanguagesType
            ).active &&
                (
                    (localProject.template.content as ProjectContentType)
                        .languages as LanguagesType
                )?.items.filter((option) => option.active === true).length > 1
        );

        setActiveLanguages(
            (
                (localProject.template.content as ProjectContentType)
                    .languages as LanguagesType
            ).items.filter((language) => language.active === true)
        );
        setKioskWays(localKioskWays);

        setProject(localProject);

        handleCloseModal();
        setNumericKeyboardInputValue("");
        setIsKeyBoardOpened(false);
        return swal({
            icon: "warning",
            content: (
                <p>
                    {t(
                        "Please note, that this modification will be overwritten the next time you synchronize."
                    )}
                </p>
            ),
            buttons: false,
            timer: 5000,
        });
    }

    React.useEffect(() => {
        setActiveTabTitle(tabs[0].title);
    }, []);

    return (
        <>
            <ConfirmationSystemActionsModelWrapper
                setSystemActions={setSystemActions}
                systemActions={systemActions}
                setIsSystemConfirmationOpen={setIsSystemConfirmationOpen}
                isSystemConfirmationOpen={isSystemConfirmationOpen}
            />
            <MenuModalWrapper
                setIsSystemConfirmationOpen={setIsSystemConfirmationOpen}
                isMenuModalOpened={isMenuModalOpened}
                setIsMenuModalOpened={setIsMenuModalOpened}
                setSystemActions={setSystemActions}
            />
            <Modal
                isOpen={isLocalConfigModalOpened}
                ariaHideApp={false}
                style={{
                    overlay: {
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "none",
                        zIndex: 2,
                    },
                    content: {
                        display: "flex",
                        flexDirection: "column",
                        padding: "0px",
                        color: "black",
                        height: "1366px",
                        width: "914px",
                        zIndex: 2,
                        backgroundColor: "#FFFFFF",
                        filter: isSystemConfirmationOpen ? "blur(5px)" : "none",
                        borderLeft: "1px solid #707070",
                        borderRight: "1px solid #707070",
                        borderBottom: "1px solid #707070",
                        borderTop: "15px",
                        borderRadius: "20px",
                        bottom: "unset",
                        top: "unset",
                        right: "unset",
                        left: "unset",
                        zoom: `${
                            process.env.REACT_APP_MODE === CATALOGUE_MODE
                                ? "50%"
                                : "100%"
                        }`,
                    },
                }}
            >
                <LocalSettingHeader />
                <div
                    style={{
                        gridTemplateColumns: "1fr",
                        gridTemplateRows: "8fr 1fr",
                        height: "1203px",
                        display: "grid",
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 4fr",
                            gridTemplateRows: "1fr",
                            margin: "20px",
                            border: "1px solid #707070",
                            borderRadius: "10px",
                        }}
                        onClick={() => {
                            setIsMenuModalOpened(false);
                        }}
                    >
                        <div
                            style={{
                                backgroundColor: "rgb(7, 93, 155)",
                                borderTopLeftRadius: "10px",
                                borderBottomLeftRadius: "10px",
                            }}
                        >
                            <Nav
                                tabs={true}
                                className="d-flex flex-column"
                                style={{
                                    border: "unset",
                                    borderRadius: "10px",
                                    marginTop: 40,
                                }}
                            >
                                {tabs.map((element, index) => {
                                    return (
                                        <NavItem
                                            key={element.title}
                                            className="pb-1"
                                            style={{
                                                borderRadius: "10px",
                                                marginBottom: 20,
                                            }}
                                        >
                                            <NavLink
                                                disabled={
                                                    (element?.title ===
                                                        t("Customer account") &&
                                                        (
                                                            localProject
                                                                .template
                                                                .content
                                                                .options as OptionsType
                                                        ).items[0]?.active ===
                                                            false) ||
                                                    (element.title ===
                                                        t(
                                                            "Information modes"
                                                        ) &&
                                                        (
                                                            localProject
                                                                .template
                                                                .content
                                                                .informationModes as ProjectContentItemType
                                                        ).items.length === 0)
                                                }
                                                className={classNames(
                                                    "border-0 menu-wrapper-nav-link__clz",
                                                    {
                                                        "active menu-wrapper-items-nav-link-active__clz":
                                                            activeTab === index,
                                                    }
                                                )}
                                                onClick={() => {
                                                    toggleNav(index);
                                                    setActiveTabTitle(
                                                        element.title
                                                    );
                                                }}
                                            >
                                                <span className="px-2">
                                                    {element.title}
                                                </span>
                                            </NavLink>
                                        </NavItem>
                                    );
                                })}
                            </Nav>
                        </div>
                        <div>
                            <TabContent activeTab={activeTab}>
                                {tabs.map((element, index) => {
                                    return (
                                        <TabPane
                                            tabId={index}
                                            key={element.title}
                                            className={classNames("h-100", {
                                                "design-active-tab__clz":
                                                    activeTab === 4,
                                            })}
                                        >
                                            {element.content}
                                        </TabPane>
                                    );
                                })}
                            </TabContent>
                        </div>
                    </div>
                    <div
                        className="d-flex align-items-center justify-content-between"
                        style={{
                            gap: "20px",
                            background:
                                "linear-gradient(135deg, #1f2937 0%, #111827 100%)",
                            padding: "1.5rem 2rem",
                            borderRadius: "0 0 20px 20px",
                            border: "1px solid #374151",
                            borderTop: "none",
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        <MenuSvgIcon
                            style={{
                                height: "75px",
                                width: "75px",
                                marginLeft: "15px",
                                cursor: "pointer",
                                fill: "#ffffff",
                                transition: "transform 0.2s ease-in-out",
                            }}
                            onClick={() => {
                                setIsMenuModalOpened(!isMenuModalOpened);
                            }}
                        />
                        <LocalSettingFooter
                            handleCloseModal={handleCloseModal}
                            handleOnClickEvent={handleOnClickEvent}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
}
